// ---------------------------------------------------------------------------
// Icons.
//
// One rule, and it is the reason this wrapper exists at all: an icon occupies
// its box before it has any geometry to draw. Two things enforce that.
//
//   1. The data is baked into the bundle by `scripts/build-icons.mjs` rather
//      than fetched by name from the Iconify API, so the path is there on the
//      first paint. A name would arrive a network round trip later and every
//      icon on the screen would pop its neighbours sideways as it landed.
//   2. `size` becomes width *and* height in pixels on the element itself, plus
//      `shrink-0`. So even if an icon ever did arrive late, the space it will
//      occupy is already reserved and nothing around it moves.
//
// Icons carry no accessible name. They are `aria-hidden` without exception, and
// anything that is only an icon needs its own label — `IconButton` requires
// one, and a bare icon beside text is decorative by definition.
// ---------------------------------------------------------------------------

import { Icon as IconifyIcon } from '@iconify/react';
import type { ReactElement } from 'react';
import type { IconData } from './icons.generated.js';

export * from './icons.generated.js';

export interface IconProps {
  icon: IconData;
  /** Pixels, square. Defaults to 16, which is the size of a line of body copy. */
  size?: number;
  className?: string;
}

export function Icon({ icon, size = 16, className }: IconProps): ReactElement {
  return (
    <IconifyIcon
      icon={icon}
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 ${className ?? ''}`}
      // Belt and braces with the width/height attributes above: a stylesheet
      // that sets `svg { width: 100% }` would otherwise win over them, and the
      // box this reserves is the whole point of the component.
      style={{ width: size, height: size }}
    />
  );
}
